"use client";
import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const DashboardPage = () => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [sales, setSales] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);

  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [loadingSales, setLoadingSales] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // counter
  const [totalOrders, setTotalOrders] = useState(0);
  const [pendingOrders, setPendingOrders] = useState(0);
  const [deliveredOrders, setDeliveredOrders] = useState(0);
  const [rtoOrders, setRtoOrders] = useState(0);

  const navigate = useNavigate();
  const authHeader = {
    headers: { authorization: localStorage.getItem("accessToken") },
  };

  useEffect(() => {
    fetchOrders();
    fetchProducts();
    fetchVendors();
    fetchSales();
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(
        "https://logicx.premierlam.com/api/pixel-get-all-order",
        authHeader
      );
      const sorted = res.data.data.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setOrders(sorted);
      // Calculate counters
      const total = sorted.length;
      const pending = sorted.filter(o => o.order_status?.toLowerCase() === "pending").length;
      

      const delivered = sorted.filter(o => o.order_status?.toLowerCase() === "order delivered").length;
      const rto = sorted.filter(o => o.order_status?.toLowerCase() === "rto processed").length;
      setTotalOrders(total);
      setPendingOrders(pending);
      setDeliveredOrders(delivered);
      setRtoOrders(rto);
    } catch (err) {
      console.log("Orders fetch failed", err);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(
        "https://logicx.premierlam.com/api/get-pixel-products",
        authHeader
      );
      const sorted = res.data.data.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setProducts(sorted);
    } catch (err) {
      console.log("Products fetch failed", err);
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await axios.get(
        "https://logicx.premierlam.com/api/pixel-get-all-vendor",
        authHeader
      );
      const sorted = res.data.data.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setVendors(sorted);
    } catch (err) {
      console.log("Vendors fetch failed", err);
    } finally {
      setLoadingVendors(false);
    }
  };

  const fetchSales = async () => {
    try {
      const res = await axios.get(
        "https://logicx.premierlam.com/api/pixel-get-all-sales",
        authHeader
      );
      const sorted = res.data.data.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setSales(sorted);
    } catch (err) {
      console.log("Sales fetch failed", err);
    } finally {
      setLoadingSales(false);
    }
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* DASHBOARD COUNTERS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">

          {/* Total Orders */}
          <div className="bg-white border rounded-lg shadow-sm p-5 flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Orders</p>
              <h2 className="text-3xl font-semibold text-gray-900">{totalOrders}</h2>
            </div>
            <div className="bg-orange-100 text-orange-600 p-3 rounded-full">
              📦
            </div>
          </div>

          {/* Pending Orders */}
          <div className="bg-white border rounded-lg shadow-sm p-5 flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Pending Orders</p>
              <h2 className="text-3xl font-semibold text-gray-900">{pendingOrders}</h2>
            </div>
            <div className="bg-yellow-100 text-yellow-600 p-3 rounded-full">
              ⏳
            </div>
          </div>

          {/* Delivered Orders */}
          <div className="bg-white border rounded-lg shadow-sm p-5 flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Delivered Orders</p>
              <h2 className="text-3xl font-semibold text-gray-900">{deliveredOrders}</h2>
            </div>
            <div className="bg-green-100 text-green-600 p-3 rounded-full">
              ✅
            </div>
          </div>
          <div className="bg-white border rounded-lg shadow-sm p-5 flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">RTO Orders</p>
              <h2 className="text-3xl font-semibold text-gray-900">{rtoOrders}</h2>
            </div>
            <div className="bg-orange-100 text-orange-600 p-3 rounded-full">
              📦
            </div>
          </div>

        </div>


        {/* ORDERS SECTION */}
        <div className="mb-12">
          <div className="flex justify-between items-center mb-3">
            <h1 className="text-2xl font-semibold text-gray-900">Orders</h1>
            <button
              onClick={() => navigate("/all-order")}
              className="text-orange-600 font-medium hover:underline"
            >
              View All
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4 overflow-x-auto">
            {loadingOrders ? (
              <div className="text-center py-5 text-gray-500">Loading...</div>
            ) : (
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b text-gray-600">
                    <th className="px-3 py-2">Order ID</th>
                    <th className="px-3 py-2">Order Date</th>
                    <th className="px-3 py-2">Customer</th>
                    <th className="px-3 py-2">Phone</th>
                    <th className="px-3 py-2">Invoice Amount</th>
                    <th className="px-3 py-2">Payment Mode</th>
                    <th className="px-3 py-2">Amount Status</th>
                    <th className="px-3 py-2">Vendor</th>
                    <th className="px-3 py-2">Items</th>
                    <th className="px-3 py-2">Shipping Label</th>
                    <th className="px-3 py-2">Order Details</th>
                    <th className="px-3 py-2">Added By</th>
                    <th className="px-3 py-2">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.slice(0, 5).map((o, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2">{o.order_id}</td>
                      <td className="px-3 py-2">{o.order_date}</td>
                      <td className="px-3 py-2">{o.customer_name}</td>
                      <td className="px-3 py-2">{o.phone_number}</td>
                      <td className="px-3 py-2">{o.invoice_amount}</td>
                      <td className="px-3 py-2">{o.payment_mode}</td>
                      <td className="px-3 py-2">{o.amount_status}</td>
                      <td className="px-3 py-2">{o.vendor_name}</td>
                      <td className="px-3 py-2">{o.number_of_items}</td>
                      <td className="px-3 py-2">{o.shipping_label}</td>
                      <td className="px-3 py-2">{o.order_details}</td>
                      <td className="px-3 py-2">{o.added_by}</td>
                      <td className="px-3 py-2">{formatDate(o.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* PRODUCTS SECTION */}
        <div className="mb-12">
          <div className="flex justify-between items-center mb-3">
            <h1 className="text-2xl font-semibold text-gray-900">Products</h1>
            <button
              onClick={() => navigate("/all-products")}
              className="text-orange-600 font-medium hover:underline"
            >
              View All
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4 overflow-x-auto">
            {loadingProducts ? (
              <div className="text-center py-5 text-gray-500">Loading...</div>
            ) : (
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b text-gray-600">
                    <th className="px-3 py-2">Image</th>
                    <th className="px-3 py-2">Code</th>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Category</th>
                    <th className="px-3 py-2">Material</th>
                    <th className="px-3 py-2">Vendor</th>
                    <th className="px-3 py-2">Cost</th>
                    <th className="px-3 py-2">Selling</th>
                    <th className="px-3 py-2 min-w-[250px]">Description</th>
                    <th className="px-3 py-2">Added By</th>
                    <th className="px-3 py-2">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {products.slice(0, 5).map((p, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <img src={p.product_image}
                          onClick={() => setPreviewImage(p.product_image)}
                          className="w-12 h-12 rounded" />
                      </td>
                      <td className="px-3 py-2">{p.product_code}</td>
                      <td className="px-3 py-2">{p.product_name}</td>
                      <td className="px-3 py-2">{p.category_name}</td>
                      <td className="px-3 py-2">{p.product_material}</td>
                      <td className="px-3 py-2">{p.vendor_name}</td>
                      <td className="px-3 py-2">{p.product_cost_price}</td>
                      <td className="px-3 py-2">{p.product_selling_price}</td>
                      <td className="px-3 py-2">{p.product_description}</td>
                      <td className="px-3 py-2">{p.added_by}</td>
                      <td className="px-3 py-2">{formatDate(p.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* VENDORS SECTION */}
        <div className="mb-12">
          <div className="flex justify-between items-center mb-3">
            <h1 className="text-2xl font-semibold text-gray-900">Vendors</h1>
            <button
              onClick={() => navigate("/all-vendors")}
              className="text-orange-600 font-medium hover:underline"
            >
              View All
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4 overflow-x-auto">
            {loadingVendors ? (
              <div className="text-center py-5 text-gray-500">Loading...</div>
            ) : (
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b text-gray-600 text-sm">
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Phone</th>
                    {/* <th className="px-3 py-2">Company</th>
                    <th className="px-3 py-2">GST</th>
                    <th className="px-3 py-2">City</th> */}
                    <th className="px-3 py-2">State</th>
                    {/* <th className="px-3 py-2">Pincode</th>
                    <th className="px-3 py-2">Vendor Code</th>
                    <th className="px-3 py-2">Bank</th>
                    <th className="px-3 py-2">IFSC</th>
                    <th className="px-3 py-2">UPI</th> */}
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {vendors.slice(0, 5).map((v) => (
                    <tr key={v.id} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2">{v.full_name}</td>
                      <td className="px-3 py-2">{v.email}</td>
                      <td className="px-3 py-2">{v.phone_number}</td>
                      {/* <td className="px-3 py-2">{v.company_name}</td>
                      <td className="px-3 py-2">{v.gst_number}</td>
                      <td className="px-3 py-2">{v.city}</td> */}
                      <td className="px-3 py-2">{v.state}</td>
                      {/* <td className="px-3 py-2">{v.pincode}</td>
                      <td className="px-3 py-2">{v.vendor_code}</td>
                      <td className="px-3 py-2">{v.bank_name}</td>
                      <td className="px-3 py-2">{v.ifsc_code}</td>
                      <td className="px-3 py-2">{v.upi_id}</td> */}
                      <td className="px-3 py-2">{v.status}</td>
                      <td className="px-3 py-2">{formatDate(v.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* SALES SECTION */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-3">
            <h1 className="text-2xl font-semibold text-gray-900">Sales</h1>
            <button
              onClick={() => navigate("/all-sales")}
              className="text-orange-600 font-medium hover:underline"
            >
              View All
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4 overflow-x-auto">
            {loadingSales ? (
              <div className="text-center py-5 text-gray-500">Loading...</div>
            ) : (
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b text-gray-600">
                    <th className="px-3 py-2">Order ID</th>
                    <th className="px-3 py-2">Product Code</th>
                    <th className="px-3 py-2">Product Name</th>
                    <th className="px-3 py-2">Qty</th>
                    <th className="px-3 py-2">Total</th>
                    <th className="px-3 py-2">Vendor</th>
                    <th className="px-3 py-2">Return?</th>
                    <th className="px-3 py-2">RTO</th>
                    <th className="px-3 py-2">Added By</th>
                    <th className="px-3 py-2">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.slice(0, 5).map((s, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2">{s.order_id}</td>
                      <td className="px-3 py-2">{s.product_master_code}</td>
                      <td className="px-3 py-2">{s.product_name}</td>
                      <td className="px-3 py-2">{s.quantity_sold}</td>
                      <td className="px-3 py-2">{s.total_price}</td>
                      <td className="px-3 py-2">{s.vendor_name}</td>
                      <td className="px-3 py-2">{s.sales_return}</td>
                      <td className="px-3 py-2">{s.rto_sale_product}</td>
                      <td className="px-3 py-2">{s.added_by}</td>
                      <td className="px-3 py-2">{formatDate(s.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {previewImage && (
              <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
                <div className="bg-white p-4 rounded-lg max-w-2xl w-full relative">
                  <button
                    className="absolute top-3 right-3 text-gray-600 hover:text-black"
                    onClick={() => setPreviewImage(null)}
                  >
                    ✖
                  </button>

                  <img
                    src={previewImage}
                    className="w-full object-contain max-h-[70vh] rounded"
                  />

                  <div className="flex justify-end mt-4">
                    <a
                      href={previewImage}
                      download
                      className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
                    >
                      Download
                    </a>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

      </div>
    </div>
  );
};

export default DashboardPage;
